// Phase 4 — native (Capacitor) storage for a rented audiobook downloaded for
// offline listening.
//
// What lives where:
//   - the encrypted .enc chapters go to the device's private app-data directory,
//     one folder per rental: offline_rentals/<hash(uid_audiobookId)>/<hash(chapterId)>.enc
//   - the small per-rental manifest (the signed token, the content key, expiresAt,
//     the per-chapter IVs and each chapter's file path) is kept in Capacitor
//     Preferences beside the files.
//
// The ciphertext on disk is never a plain ownable audio file; it is only ever
// decrypted in memory at playback time (see offlineRentalCrypto.js). The threat
// model already accepts that a rooted device can recover the key, so the storage
// is deliberately simple — but the token and the content key are NEVER logged.
//
// Atomic commit + resumable: the Preferences manifest is written only once every
// chapter is on disk, so an interrupted download never leaves a usable-but-broken
// record. A retry skips chapters already written (resume) and commits when whole.
//
// Each chapter is streamed straight to disk by @capacitor/file-transfer (the
// supported successor to the deprecated Filesystem.downloadFile), so the encrypted
// bytes never pass through JS memory. This also sidesteps the WebView CORS layer,
// but the bucket CORS policy is still required by the plaintext offline cache in
// offlineStorage.js, so it must not be removed.
//
// Everything is gated behind isNative(): on the web portal these are no-ops or
// return "not downloaded", so offline download degrades gracefully to streaming.
//
// The core is built by createRentalStore(deps) so it can be unit-tested with fake
// Filesystem/Preferences/FileTransfer handles; the default export wires the real
// Capacitor plugins.

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { FileTransfer } from "@capacitor/file-transfer";

const PREF_PREFIX = "offline_rental_";
const ROOT_DIR = "offline_rentals";

// SHA-256 hex of a string, used to derive opaque, filesystem-safe folder and file
// names from the uid, audiobookId and chapterId.
async function sha256Hex(subtle, value) {
  const data = new TextEncoder().encode(value);
  const digest = await subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Build the offline-rental store against injected handles. `directory` is the
// Capacitor Directory enum value the files live under; `subtle()` yields a
// SubtleCrypto; `fileTransfer` streams a signed chapter URL straight to a file
// path; `isNativeFn` gates the whole thing off on web.
export function createRentalStore({
  Filesystem: fs,
  directory,
  Preferences: prefs,
  FileTransfer: fileTransfer,
  subtle,
  isNativeFn,
  nowIso = () => new Date().toISOString(),
}) {
  const isNative = () => Boolean(isNativeFn && isNativeFn());
  const getSubtle = () => subtle();

  const prefKeyFor = async (uid, audiobookId) =>
    `${PREF_PREFIX}${await sha256Hex(getSubtle(), `${uid}_${audiobookId}`)}`;
  const dirFor = async (uid, audiobookId) =>
    `${ROOT_DIR}/${await sha256Hex(getSubtle(), `${uid}_${audiobookId}`)}`;

  async function fileExists(path) {
    try {
      await fs.stat({ path, directory });
      return true;
    } catch {
      return false;
    }
  }

  // Download every encrypted chapter in `bundle` (the prepareOfflineBundle result)
  // and, once all are on disk, commit the manifest. onProgress is called with
  // { done, total } after each chapter. Resumable: chapters already present are
  // skipped. Throws on a failed download, leaving no committed manifest.
  async function downloadRentalBundle(uid, audiobookId, bundle, { onProgress, bookMeta } = {}) {
    if (!isNative()) {
      throw new Error("Offline download is a native-app feature");
    }
    if (!bundle || !Array.isArray(bundle.chapters) || bundle.chapters.length === 0) {
      throw new Error("Offline bundle has no chapters");
    }

    const dir = await dirFor(uid, audiobookId);
    const total = bundle.chapters.length;
    const chapterRecords = [];

    // file-transfer writes to an absolute path and does not create parent dirs, so
    // make sure the per-rental folder exists before any chapter is streamed in.
    try {
      await fs.mkdir({ path: dir, directory, recursive: true });
    } catch {
      // Already there from a prior (possibly interrupted) download — fine.
    }

    // Commit the Preferences manifest for the chapters currently on disk. Called
    // once every chapter has landed (full download) OR with the partial set when a
    // chapter download fails part-way (e.g. the device runs out of space), so a
    // partial download is still playable rather than orphaned and unusable.
    const commitManifest = async (records) => {
      const record = {
        audiobookId,
        uid,
        expiresAt: bundle.expiresAt,
        algorithm: bundle.algorithm || null,
        token: bundle.token,
        contentKey: bundle.contentKey,
        dir,
        downloadedAt: nowIso(),
        // Book display metadata for the offline library + player (no secrets).
        book: bookMeta ? {
          audiobookId,
          authorId: bookMeta.authorId || null,
          title: bookMeta.title || null,
          authorName: bookMeta.authorName || null,
          coverUrl: bookMeta.coverUrl || null,
        } : null,
        chapters: records,
      };
      await prefs.set({
        key: await prefKeyFor(uid, audiobookId),
        value: JSON.stringify(record),
      });
    };

    for (let i = 0; i < total; i++) {
      const ch = bundle.chapters[i];
      const file = `${dir}/${await sha256Hex(getSubtle(), ch.chapterId)}.enc`;

      // Skip only when the FINAL file exists. A half-written download lives at
      // file.part and is never mistaken for a complete chapter, so an interrupted
      // download is re-fetched on retry rather than skipped.
      if (!(await fileExists(file))) {
        // Stream straight to a temporary path on disk — the encrypted bytes never
        // enter JS memory — then rename into place only once the download has
        // completed, so the final path appears atomically and is whole.
        const partFile = `${file}.part`;
        const { uri } = await fs.getUri({ path: partFile, directory });
        try {
          await fileTransfer.downloadFile({ url: ch.url, path: uri });
        } catch (err) {
          const status =
            (err && err.data && (err.data.httpStatus || err.data.code)) ||
            (err && err.message) ||
            "download failed";
          // Best-effort: did the device run out of storage? The exact wording
          // varies by OS/plugin version, so match loosely and fall back to a
          // generic download error if it doesn't look like a space problem.
          const isNoSpace = /space|enospc|full|storage/i.test(
            `${status} ${err && err.message ? err.message : ""}`,
          );
          // Commit whatever completed so the partial download stays PLAYABLE,
          // then surface a typed error the caller can act on.
          if (chapterRecords.length > 0) {
            await commitManifest(chapterRecords);
          }
          const e = new Error(`Could not download chapter ${ch.chapterId} (${status})`);
          e.code = isNoSpace ? "no-space" : "download-failed";
          e.done = chapterRecords.length;
          e.total = total;
          throw e;
        }
        await fs.rename({ from: partFile, to: file, directory });
      }

      // Attach display metadata (title/order) so the offline library + player can
      // render and order chapters with ZERO Firestore reads.
      const chMeta = bookMeta && Array.isArray(bookMeta.chapters)
        ? bookMeta.chapters.find((c) => c.id === ch.chapterId)
        : null;
      chapterRecords.push({
        chapterId: ch.chapterId,
        iv: ch.iv,
        file,
        title: chMeta?.title || null,
        order: typeof chMeta?.order === "number" ? chMeta.order : i,
      });
      if (onProgress) onProgress({ done: i + 1, total });
    }

    // Atomic commit — only now that every chapter is on disk.
    await commitManifest(chapterRecords);
    return { chapters: total };
  }

  // The committed manifest for a downloaded rental, or null. Holds the token and
  // content key — never log the returned object.
  async function getRentalDownload(uid, audiobookId) {
    if (!isNative()) return null;
    try {
      const { value } = await prefs.get({ key: await prefKeyFor(uid, audiobookId) });
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }

  async function isRentalDownloaded(uid, audiobookId) {
    return Boolean(await getRentalDownload(uid, audiobookId));
  }

  // Every committed offline-rental manifest on this device. Lets the library list
  // downloads when Firestore is unreachable. Holds tokens/keys — never log the
  // returned objects. Native only; [] on web.
  async function listRentalDownloads() {
    if (!isNative()) return [];
    let keys = [];
    try {
      const res = await prefs.keys();
      keys = (res?.keys || []).filter((k) => k.startsWith(PREF_PREFIX));
    } catch {
      return [];
    }
    const records = [];
    for (const key of keys) {
      try {
        const { value } = await prefs.get({ key });
        if (value) records.push(JSON.parse(value));
      } catch {
        // Skip a corrupt entry rather than failing the whole list.
      }
    }
    return records;
  }

  // Read one chapter's encrypted bytes back as base64 (ciphertext||tag), ready to
  // hand to decryptChapterData. Returns null if the rental or chapter is not
  // downloaded.
  async function readEncryptedChapterData(uid, audiobookId, chapterId) {
    if (!isNative()) return null;
    const record = await getRentalDownload(uid, audiobookId);
    if (!record) return null;
    const chapter = record.chapters.find((c) => c.chapterId === chapterId);
    if (!chapter) return null;
    const { data } = await fs.readFile({ path: chapter.file, directory });
    return data; // base64
  }

  // Delete every local .enc file and ALL stored key material for this rental:
  // wipe the per-rental directory (deterministic, so this works even after a
  // partial download) and remove the Preferences manifest holding the token and
  // key.
  async function removeRentalDownload(uid, audiobookId) {
    if (!isNative()) return;
    const dir = await dirFor(uid, audiobookId);
    try {
      await fs.rmdir({ path: dir, directory, recursive: true });
    } catch {
      // Already gone — nothing to remove.
    }
    try {
      await prefs.remove({ key: await prefKeyFor(uid, audiobookId) });
    } catch {
      // Best effort.
    }
  }

  return {
    downloadRentalBundle,
    getRentalDownload,
    isRentalDownloaded,
    listRentalDownloads,
    readEncryptedChapterData,
    removeRentalDownload,
  };
}

// Whether offline rental download is available in this environment (native only).
export function isRentalOfflineSupported() {
  return Capacitor.isNativePlatform();
}

// The real store, wired to the live Capacitor plugins. Files live under the
// app-private Data directory.
const realStore = createRentalStore({
  Filesystem,
  directory: Directory.Data,
  Preferences,
  FileTransfer,
  subtle: () => window.crypto.subtle,
  isNativeFn: () => Capacitor.isNativePlatform(),
});

export const {
  downloadRentalBundle,
  getRentalDownload,
  isRentalDownloaded,
  listRentalDownloads,
  readEncryptedChapterData,
  removeRentalDownload,
} = realStore;
